import { isFlightItem } from 'checkout/lib/utils/flights/cart'
import { getInflowFlightsSearchUrl, getStandaloneFlightsSearchUrl } from 'checkout/lib/utils/flights/links'
import { partitionBy, sum } from 'lib/arrayUtils'
import {
  buildFlightItemBreakdownView,
  formatFareType,
  formatFlightPassengersShorthand,
  formatJourneyShorthand,
} from 'lib/checkout/flights/format'
import createSelector from 'lib/createSelector'
import { arrayResultMemoizeOptions } from 'checkout/lib/utils/misc/arrayResultMemoizeOptions'
import { computeFlightPrice } from 'checkout/lib/utils/flights/price'

export const getFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items.filter(isFlightItem),
  arrayResultMemoizeOptions,
)

export const cartIncludesFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.some(isFlightItem),
)

function mapFlightToView(item: App.Checkout.FlightItem, journey: App.Journey): App.Checkout.FlightItemView {
  const fees = item.otherFees ?
    sum([
      item.otherFees.flightCreditFee,
      item.otherFees.flightBookingFee,
      item.otherFees.flightAtolFee,
      item.otherFees.flightServiceFee,
    ]) : 0

  const journeyDefinedPrice = computeFlightPrice(journey, Boolean(item.isBundled), Boolean(item.viewType))
  const price = (item.totalFare || journeyDefinedPrice)
  return {
    itemId: item.itemId,
    item,
    journeyId: item.searchId,
    provider: journey.provider,
    passengers: item.passengers,
    departing: {
      journeyFlight: journey.departing,
      extras: item.departing.extras,
      cost: item.departing.cost,
    },
    ...(item.returning && journey.returning && {
      returning: {
        journeyFlight: item.returning && journey.returning,
        extras: item.returning.extras,
        cost: item.returning.cost,
      },
    }),
    designation: 'Flights',
    validatingCarrier: journey.validatingCarrier,
    validatingCarrierName: journey.validatingCarrierName || '',
    validatingCarrierLogo: journey.validatingCarrierLogo || '',
    fareType: item.fareType,
    price: item.isBundled ? price + fees : price,
    value: 0, // No data on this
    surcharge: journey.fee,
    seatSelectionPolicy: journey.seatSelectionPolicy,
    isBundled: item.isBundled,
    taxesAndFees: 0,
    otherFees: !item.isBundled ? item.otherFees : undefined,
    searchFlightsUrl: item.isBundled ?
      getInflowFlightsSearchUrl(journey) :
      getStandaloneFlightsSearchUrl(journey, item),
  }
}

export const getFlightItemsView = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeys,
  (cartItems, journeys): App.WithDataStatus<Array<App.Checkout.FlightItemView>> => {
    const isFetchingFares = cartItems.some(item => item.isFetchingFares)
    const savedJourneyMap = journeys.reduce((acc, item) => {
      const itemId = item.id
      return {
        ...acc,
        ...(itemId && { [itemId]: item }),
      }
    }, {})

    const [loadedJourneys, missingJourneys] = partitionBy<App.Checkout.FlightItem>(cartItems, item => item.searchId in savedJourneyMap)
    const flightViews = loadedJourneys.map<App.Checkout.FlightItemView>(journeyItem =>
      mapFlightToView(journeyItem, savedJourneyMap[journeyItem.searchId]),
    )

    return {
      hasRequiredData: !isFetchingFares && missingJourneys.length === 0,
      data: flightViews,
    }
  },
)

export const getFlightBreakdownView = createSelector(
  getFlightItemsView,
  (flightViewsWithStatus): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    const flightBreakdownViews = flightViewsWithStatus.data.map<App.Checkout.PriceBreakdownView>(view => {
      const supplementaryData = {
        validatingCarrierName: view.validatingCarrierName,
        validatingCarrierLogo: view.validatingCarrierLogo,
      }
      const breakdownView = {
        title: formatFareType(view.fareType),
        additionalInfoText: [
          formatJourneyShorthand(view.departing.journeyFlight),
          formatFlightPassengersShorthand(view.passengers),
        ],
        price: view.isBundled ? 0 : sum(flightViewsWithStatus.data.map(item => item?.price ?? 0)),
        items: [
          buildFlightItemBreakdownView(view, view.departing, supplementaryData),
          ...(view.returning ? [buildFlightItemBreakdownView(view, view.returning, supplementaryData)] : []),
        ],
      }
      return breakdownView
    })

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData,
      data: flightBreakdownViews,
    }
  },
)
